import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { ErrorModal } from '../components/ui/ErrorModal';
import {
  ArrowLeft,
  Loader2,
  Bell,
  Smartphone,
  Volume2,
  ShieldCheck,
  Wrench,
  Megaphone,
  Tag,
  Play,
  Video,
  Moon,
  AlertTriangle,
  Sparkles,
  Check,
} from 'lucide-react';
import { APP_CONFIG } from '../config/app-config';
import { getToken } from '../utils/authToken';

interface NotificationSettings {
  inAppEnabled: boolean;
  pushEnabled: boolean;
  soundEnabled: boolean;
  flashBell: boolean;
  freezeOnBankCredit: boolean;
  freezeOnWalletCredit: boolean;
  maintenanceEnabled: boolean;
  marketingEnabled: boolean;
  marketingSpecialsEnabled: boolean;
  marketingWatchToEarnEnabled: boolean;
  marketingLiveStreamEnabled: boolean;
  dndStart: string | null;
  dndEnd: string | null;
}

const DEFAULT_SETTINGS: NotificationSettings = {
  inAppEnabled: true,
  pushEnabled: true,
  soundEnabled: false,
  flashBell: true,
  freezeOnBankCredit: true,
  freezeOnWalletCredit: false,
  maintenanceEnabled: true,
  marketingEnabled: false,
  marketingSpecialsEnabled: false,
  marketingWatchToEarnEnabled: false,
  marketingLiveStreamEnabled: false,
  dndStart: null,
  dndEnd: null,
};

const EDITABLE_FIELDS: (keyof NotificationSettings)[] = [
  'inAppEnabled',
  'pushEnabled',
  'soundEnabled',
  'flashBell',
  'freezeOnBankCredit',
  'freezeOnWalletCredit',
  'maintenanceEnabled',
  'marketingEnabled',
  'marketingSpecialsEnabled',
  'marketingWatchToEarnEnabled',
  'marketingLiveStreamEnabled',
  'dndStart',
  'dndEnd',
];

interface ToggleRowProps {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

function ToggleRow({ id, icon, title, description, checked, onChange, disabled }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-3" style={{ opacity: disabled ? 0.55 : 1 }}>
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: '#f1f5f9',
            color: '#2D8CCA',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div>
          <Label htmlFor={id} style={{ fontWeight: 600, color: '#1f2937' }}>
            {title}
          </Label>
          <p className="text-sm text-gray-600" style={{ marginTop: '2px' }}>
            {description}
          </p>
        </div>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

export function NotificationSettingsPage() {
  const navigate = useNavigate();

  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [errorModal, setErrorModal] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  // Prevents the auto-save effect from firing on the initial load.
  const loadedRef = useRef(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dndEnabled = Boolean(settings.dndStart && settings.dndEnd);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const res = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/notifications/settings`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error('Failed to load notification settings');
      }
      const data = await res.json();
      if (data.success && data.data) {
        setSettings((prev) => ({
          ...prev,
          ...Object.fromEntries(
            EDITABLE_FIELDS.map((f) => [f, data.data[f] ?? prev[f]])
          ),
        }) as NotificationSettings);
      }
    } catch (err) {
      setErrorModal({
        open: true,
        message: 'We could not load your notification settings. Please try again.',
      });
    } finally {
      setLoading(false);
      // Allow auto-save only after the first successful load.
      setTimeout(() => {
        loadedRef.current = true;
      }, 0);
    }
  };

  const saveSettings = async (next: NotificationSettings) => {
    try {
      setSaving(true);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const body: Partial<NotificationSettings> = {};
      EDITABLE_FIELDS.forEach((f) => {
        (body as Record<string, unknown>)[f] = next[f];
      });
      const res = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/notifications/settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error('Failed to save notification settings');
      }
      // Gentle, non-blocking confirmation that fades on its own.
      setJustSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setJustSaved(false), 1600);
    } catch (err) {
      setErrorModal({
        open: true,
        message: 'We could not save your notification settings. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadSettings();
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  // Update the wallet bell immediately when the flash-bell preference changes.
  useEffect(() => {
    if (!loadedRef.current) return;
    window.dispatchEvent(
      new CustomEvent('notificationSettingsUpdated', {
        detail: { flashBell: settings.flashBell },
      })
    );
  }, [settings.flashBell]);

  // Debounced auto-save whenever settings change (after first load).
  useEffect(() => {
    if (!loadedRef.current || loading) return;
    const timeoutId = setTimeout(() => {
      saveSettings(settings);
    }, 800);
    return () => clearTimeout(timeoutId);
  }, [settings, loading]);

  const update = (field: keyof NotificationSettings, value: boolean | string | null) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const toggleDnd = (value: boolean) => {
    if (value) {
      setSettings((prev) => ({ ...prev, dndStart: '22:00', dndEnd: '07:00' }));
    } else {
      setSettings((prev) => ({ ...prev, dndStart: null, dndEnd: null }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#86BE41]" />
          <p className="text-gray-600">Loading notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#ffffff', minHeight: 'auto', fontFamily: 'Montserrat, sans-serif' }}>
      {/* Sticky header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '64px',
            padding: '0 16px',
          }}
        >
          <button
            onClick={() => navigate('/profile')}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: 'transparent',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              fontFamily: 'Montserrat, sans-serif',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            aria-label="Go back"
          >
            <ArrowLeft style={{ width: '24px', height: '24px', color: '#6b7280' }} />
          </button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <h1 style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '20px', fontWeight: 700, color: '#1f2937', margin: 0 }}>
              Notification Settings
            </h1>
          </div>
          {/* Subtle, fixed-width autosave indicator (no layout shift, no flashing banner) */}
          <div
            style={{ width: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-live="polite"
          >
            {saving ? (
              <Loader2 style={{ width: '18px', height: '18px', color: '#9ca3af' }} className="animate-spin" aria-label="Saving" />
            ) : justSaved ? (
              <Check style={{ width: '18px', height: '18px', color: '#86BE41' }} aria-label="Saved" />
            ) : null}
          </div>
        </div>
      </div>

      <div style={{ padding: 'var(--mobile-padding)' }}>
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'var(--mobile-font-base)',
            color: '#6b7280',
            marginTop: '0.5rem',
            marginBottom: '1.5rem',
          }}
        >
          Choose what you hear from MyMoolah and how. You can change these anytime.
        </p>

        {/* Delivery */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#86BE41]" />
              Delivery
            </CardTitle>
            <p className="text-sm text-gray-600">How MyMoolah reaches you.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-5">
              <ToggleRow
                id="inAppEnabled"
                icon={<Bell className="w-4 h-4" />}
                title="In-app notifications"
                description="Show alerts and updates inside the app."
                checked={settings.inAppEnabled}
                onChange={(v) => update('inAppEnabled', v)}
              />
              <ToggleRow
                id="pushEnabled"
                icon={<Smartphone className="w-4 h-4" />}
                title="Push notifications"
                description="Get alerts on your device, even when the app is closed."
                checked={settings.pushEnabled}
                onChange={(v) => update('pushEnabled', v)}
              />
              <ToggleRow
                id="soundEnabled"
                icon={<Volume2 className="w-4 h-4" />}
                title="Sound"
                description="Play a sound for new notifications."
                checked={settings.soundEnabled}
                onChange={(v) => update('soundEnabled', v)}
              />
              <ToggleRow
                id="flashBell"
                icon={<Bell className="w-4 h-4" />}
                title="Flash the bell"
                description="Animate the bell icon when something new arrives."
                checked={settings.flashBell}
                onChange={(v) => update('flashBell', v)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Transactions & Security */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#86BE41]" />
              Transactions & Security
            </CardTitle>
            <p className="text-sm text-gray-600">
              Payment, deposit and security alerts are always on to keep your money safe.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-5">
              <ToggleRow
                id="freezeOnBankCredit"
                icon={<ShieldCheck className="w-4 h-4" />}
                title="Confirm bank deposits"
                description="Require a tap to acknowledge incoming bank deposits."
                checked={settings.freezeOnBankCredit}
                onChange={(v) => update('freezeOnBankCredit', v)}
              />
              <ToggleRow
                id="freezeOnWalletCredit"
                icon={<ShieldCheck className="w-4 h-4" />}
                title="Confirm wallet credits"
                description="Require a tap to acknowledge money received in your wallet."
                checked={settings.freezeOnWalletCredit}
                onChange={(v) => update('freezeOnWalletCredit', v)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Service updates */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-[#86BE41]" />
              Service Updates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ToggleRow
              id="maintenanceEnabled"
              icon={<Wrench className="w-4 h-4" />}
              title="Product & maintenance notices"
              description="Planned maintenance, new features and important service updates."
              checked={settings.maintenanceEnabled}
              onChange={(v) => update('maintenanceEnabled', v)}
            />
          </CardContent>
        </Card>

        {/* Marketing & Rewards */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#86BE41]" />
              Marketing & Rewards
            </CardTitle>
            <p className="text-sm text-gray-600">
              Optional. We only send these with your permission, and you can opt out anytime.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-5">
              <ToggleRow
                id="marketingEnabled"
                icon={<Megaphone className="w-4 h-4" />}
                title="Allow marketing notifications"
                description="Master switch for all promotional messages below."
                checked={settings.marketingEnabled}
                onChange={(v) => update('marketingEnabled', v)}
              />
              <div
                style={{
                  borderLeft: '2px solid #e2e8f0',
                  paddingLeft: '14px',
                  marginLeft: '4px',
                }}
                className="space-y-5"
              >
                <ToggleRow
                  id="marketingSpecialsEnabled"
                  icon={<Tag className="w-4 h-4" />}
                  title="Product specials & deals"
                  description="Discounts, cashback and limited-time offers."
                  checked={settings.marketingSpecialsEnabled}
                  onChange={(v) => update('marketingSpecialsEnabled', v)}
                  disabled={!settings.marketingEnabled}
                />
                <ToggleRow
                  id="marketingWatchToEarnEnabled"
                  icon={<Play className="w-4 h-4" />}
                  title="Watch-to-earn"
                  description="New rewarded videos and earning opportunities."
                  checked={settings.marketingWatchToEarnEnabled}
                  onChange={(v) => update('marketingWatchToEarnEnabled', v)}
                  disabled={!settings.marketingEnabled}
                />
                <ToggleRow
                  id="marketingLiveStreamEnabled"
                  icon={<Video className="w-4 h-4" />}
                  title="Live video streaming"
                  description="Going-live alerts for new live streaming services."
                  checked={settings.marketingLiveStreamEnabled}
                  onChange={(v) => update('marketingLiveStreamEnabled', v)}
                  disabled={!settings.marketingEnabled}
                />
              </div>
              <p className="text-xs text-gray-500">
                By enabling marketing notifications you consent to receive promotional messages from
                MyMoolah. We never share your personal information for third-party marketing (POPIA).
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Quiet hours */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Moon className="w-5 h-5 text-[#86BE41]" />
              Quiet Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-5">
              <ToggleRow
                id="dndEnabled"
                icon={<Moon className="w-4 h-4" />}
                title="Do Not Disturb"
                description="Pause non-urgent notifications during these hours."
                checked={dndEnabled}
                onChange={toggleDnd}
              />
              {dndEnabled && (
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label htmlFor="dndStart" className="text-sm">From</Label>
                    <input
                      id="dndStart"
                      type="time"
                      value={settings.dndStart ?? '22:00'}
                      onChange={(e) => update('dndStart', e.target.value)}
                      className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="dndEnd" className="text-sm">To</Label>
                    <input
                      id="dndEnd"
                      type="time"
                      value={settings.dndEnd ?? '07:00'}
                      onChange={(e) => update('dndEnd', e.target.value)}
                      className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    />
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs text-blue-800 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  Transaction and security alerts are always delivered, even during Quiet Hours.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <ErrorModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: '' })}
        title="Notification Settings"
        message={errorModal.message}
        type="error"
      />
    </div>
  );
}

export default NotificationSettingsPage;
